import React from 'react';

/**
 * TrustChip — composite Trust Score + Autonomy Tier indicator.
 *
 * Per Design Principles §5.2, every Agentlet displays its trust score
 * (0-100) and autonomy tier as a compact chip used everywhere the
 * agent appears (inventory rows, slide-outs, detail headers, fleet
 * preview tables, governance dashboards).
 *
 * Visual: 4 tier dots + numeric score, tone-coded by trust band.
 *
 * G330 — Tone thresholds (≥90 success, ≥75 warning, <75 danger) now
 * match the canonical trustBand() in AgentTrustCenterPageV2 and the
 * TrustGauge arc-color band. Previously the chip rendered success
 * tone at ≥80, which surfaced a green chip for a score of 85 even
 * though the same agent was classified as "medium" in the Dashboard
 * band distribution and amber on the Board Report gauge — visually
 * inconsistent across surfaces. Dot count still reflects the 4-tier
 * autonomy ladder (≥90→4, ≥80→3, ≥75→2, <75→1) so an at-the-edge
 * agent reads as "still has runway" but the tone honestly mirrors
 * the band.
 *
 * Dot tier (4 → 1):
 *   ≥ 90  → 4 dots filled  (success tone)
 *   80–89 → 3 dots filled  (warning tone)
 *   75–79 → 2 dots filled  (warning tone)
 *   < 75  → 1 dot  filled  (danger  tone, auto-probation per §7)
 *
 * The autonomy prop, if supplied, overrides the score-derived dot count
 * for cases where the platform has demoted an agent below its raw score
 * (e.g. a recent BLOCK on a 92-trust agent now sitting at SUPERVISED).
 *
 * @param {Object} props
 * @param {number} props.score — 0-100 trust score
 * @param {'AUTONOMOUS'|'SUPERVISED'|'RESTRICTED'|'PROBATION'} [props.autonomy]
 * @param {'sm'|'md'} [props.size='md']
 * @param {string}    [props.className]
 */
import { publishedBand, displayScore, autonomyBand } from '../../services/trustBands';

/** Tone per published band. The chip colours on the same ramp as every dial. */
const BAND_TONE = {
  strong:   'success',
  adequate: 'success',
  limited:  'warning',
  weak:     'danger',
  critical: 'danger',
};

export function TrustChip({ score, autonomy, size = 'md', className = '' }) {
  const AUTONOMY_TIERS = {
    AUTONOMOUS: 4,
    SUPERVISED: 3,
    RESTRICTED: 2,
    PROBATION: 1,
  };

  const sizeClass = size === 'sm'
    ? 'h-[18px] px-2 py-px text-[10.5px] gap-1'
    : 'h-[20px] px-2.5 py-0.5 text-[11.5px] gap-1.5';
  const dotSize = size === 'sm' ? 'w-1 h-1' : 'w-[5px] h-[5px]';

  // Honest "Unrated" — null/undefined/non-finite score = no governed evidence yet.
  // (A real numeric 0 is a legitimate at-risk score, NOT Unrated.) This lets every
  // call site pass the canonical complianceScore (null = Unrated) without fabricating
  // a 0/75/100. See docs/design/TRUST_SCORE_SINGLE_SOURCE_RC.md.
  const numeric = (score === null || score === undefined || Number.isNaN(Number(score)))
    ? null : Number(score);
  if (numeric === null) {
    return (
      <span
        role="status"
        aria-label="Trust score unrated — no protected runs yet"
        title="Unrated — no protected runs yet"
        className={
          'inline-flex items-center font-semibold whitespace-nowrap select-none ' +
          'bg-v2-muted border border-v2-border-subtle rounded-v2-pill ' +
          sizeClass + ' text-v2-ink-muted ' + className
        }
      >
        <span aria-hidden="true" className="flex items-center gap-px">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={'rounded-full ' + dotSize + ' bg-v2-border-strong'} />
          ))}
        </span>
        <span className="tabular-nums">Unrated</span>
      </span>
    );
  }

  /* Rounded DOWN, through the same helper the dials use. This read Math.round, so one agent
   * showed 78 on a chip and 77 on the dial two inches below it — the same 77.91, twice. */
  const safeScore = displayScore(numeric);

  /* The dots show how much autonomy the score supports, which is the AUTONOMY question and not
   * the band one — different thresholds, deliberately. This carried a third set (90/80/75) that
   * matched neither the bands nor the configured autonomy thresholds, so a chip could show three
   * dots for a score the platform would never promote. Resolved from the shared service now. */
  const AUTONOMY_DOTS = { strong: 4, adequate: 3, limited: 2, critical: 1, unrated: 1 };
  const tierFromScore = AUTONOMY_DOTS[autonomyBand(safeScore)] || 1;

  const filled = autonomy && AUTONOMY_TIERS[autonomy] != null
    ? AUTONOMY_TIERS[autonomy]
    : tierFromScore;

  /* The published band decides the tone. G330 aligned this chip with a 90/75 ramp that lived in
   * utils/trustScore.js; that ramp is gone and there is now one band set across the product, so
   * the chip resolves it from the same service as the scorecard rather than restating it. */
  const tone = BAND_TONE[publishedBand(safeScore)] || 'danger';

  const TONE_TEXT = {
    success: 'text-v2-success',
    warning: 'text-v2-warning',
    danger:  'text-v2-danger',
  };

  const TONE_DOT = {
    success: 'bg-v2-success',
    warning: 'bg-v2-warning',
    danger:  'bg-v2-danger',
  };

  const ariaLabel =
    `Trust score ${safeScore} of 100` +
    (autonomy ? `, autonomy ${autonomy.toLowerCase()}` : '');

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      className={
        'inline-flex items-center font-semibold whitespace-nowrap select-none ' +
        'bg-v2-muted border border-v2-border-subtle rounded-v2-pill ' +
        sizeClass + ' ' + TONE_TEXT[tone] + ' ' + className
      }
    >
      <span aria-hidden="true" className="flex items-center gap-px">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={
              'rounded-full ' + dotSize + ' ' +
              (i < filled ? TONE_DOT[tone] : 'bg-v2-border-strong')
            }
          />
        ))}
      </span>
      <span className="font-mono-v2 tabular-nums">{safeScore}</span>
    </span>
  );
}

export default TrustChip;
